import { ApplicationGameConfig } from '@cgs/shared';
import { ASSETS_URL_FORMATTER_TOKEN, Container, IAssetsUrlFormatter } from '@cgs/wge';
import { SLOT_GAME_SETTINGS_TOKEN, SlotGameSettings } from '../../slot-game-settings';
import { CheatManager, CheatManagerToken } from '../cheat-manager';
import { IPredefinedReel } from '../types';
import { CheatPopupElementToken } from './cheat-popup';
import { CheatUISection } from './cheat-ui-section';
import { getCheatUIStyles } from './styles';

export const CheatReelsToken = Symbol('CheatReels');

export class CheatReelsUI extends CheatUISection {
  private selectedIcon: string | null = null;
  private reels: IPredefinedReel[] = [];
  private readonly assetsUrlFormatter: IAssetsUrlFormatter | null;

  private storageKey: string = 'cheatReels';

  constructor(private container: Container) {
    super();

    this.assetsUrlFormatter = container.resolve<IAssetsUrlFormatter>(ASSETS_URL_FORMATTER_TOKEN, {
      optional: true,
    });

    if (container.resolve<boolean>(CheatReelsToken)) {
      this.initialize();
    }
  }

  public initialize(): void {
    this.storageKey = `${ApplicationGameConfig.gameId}-cheatReels`;

    const consoleEl = this.container.resolve<HTMLDivElement>(CheatPopupElementToken);
    this.initializeBaseUI(consoleEl);
    const cheatUrl = this.container
      .resolve<SlotGameSettings>(SLOT_GAME_SETTINGS_TOKEN)
      .getCheatUrl();

    fetch(cheatUrl)
      .then((response) => response.json())
      .then((data) => data?.predefinedReels || [])
      .then((predefinedReels) => {
        const savedReels = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
        this.reels = [...predefinedReels, ...savedReels];
        this.setupUI(this.reels);
        this.setupEventListeners();
      })
      .catch(() => {
        throw new Error(`Failed to load cheat data from ${cheatUrl}`);
      });
  }

  private setupUI(predefinedReels: IPredefinedReel[]): void {
    const styles = getCheatUIStyles();
    const content = `
      <div style="${styles.sectionStyle}">
        <div style="margin-bottom: 8px; position: relative;">
          <button id="applyReels" style="${styles.buttonStyle}">Apply</button>
          <button id="applyReelsOnce" style="${styles.buttonStyle}">Apply Once</button>
          <button id="saveReels" style="${styles.buttonStyle}">Save custom reels</button>
          <div id="saveReelsPopup" style="${styles.popup} top:0; left:0; transform: none;">
            <div style="width: 100%;${styles.sectionStyle}">
              <input id="customReelsName" type="text" style="${styles.inputStyle}" />
              <div style="margin-bottom: 8px;">
                <button id="saveReelsConfirm" style="${styles.buttonStyle}">Save</button>
                <button id="saveReelsCancel" style="${styles.buttonStyle}">Cancel</button>
              </div>
            </div>
          </div>
        </div>
        <div style="display: flex; min-height: 34px;">
          <select id="predefinedReels" style="${styles.selectStyle}">
            ${this.renderReelsOptions(predefinedReels)}    
          </select>
          <button id="deleteReels" style="display: none; ${styles.buttonStyle}">Delete custom reels</button>
        </div>
        <div id="reelsPreview"></div>
         <input type="text" id="reels" value="${JSON.stringify(
           predefinedReels[0]?.value || []
         )}" style="${styles.inputStyle}">
         
        </div>

        <div style="${styles.sectionStyle}">
          ${this.createIconSelectionUI()}
        </div>
      </div>`;

    this.contentEl.innerHTML = content;

    // Initialize preview for the first reel set
    if (predefinedReels.length > 0) {
      const previewEl = this.getChildEl<HTMLDivElement>('#reelsPreview');
      if (previewEl) {
        previewEl.innerHTML = this.createSymbolPreview(predefinedReels[0].value);
      }
    }
  }

  private renderReelsOptions(predefinedReels: IPredefinedReel[]): string {
    return predefinedReels
      .map((reel) => {
        const isLocal = reel.isLocal || false;
        const name = isLocal ? `[Local] ${reel.id}` : reel.id;
        return `<option value="${JSON.stringify(reel.value)}" data-is-local=${isLocal} data-id="${reel.id}">${name}</option>`;
      })
      .join('');
  }

  private setupEventListeners(): void {
    // Reels event listeners

    const cheatManager = this.container.resolve<CheatManager>(CheatManagerToken);

    this.getChildEl<HTMLInputElement>('#reels')?.addEventListener('blur', () => {
      const inputEl = this.getChildEl<HTMLInputElement>('#reels');
      if (inputEl && inputEl.value) {
        try {
          const reels = JSON.parse(inputEl.value);
          const reelsPreviewEl = this.getChildEl<HTMLDivElement>('#reelsPreview');
          if (reelsPreviewEl) {
            reelsPreviewEl.innerHTML = this.createSymbolPreview(reels);
          }
        } catch (e) {
          console.error('Invalid reels format');
        }
      }
    });

    this.getChildEl<HTMLButtonElement>('#applyReels')?.addEventListener('click', () => {
      const inputEl = this.getChildEl<HTMLInputElement>('#reels');
      if (inputEl && inputEl.value) {
        try {
          const reels = JSON.parse(inputEl.value);
          cheatManager.applyReelSet(reels);

          const reelsPreviewEl = this.getChildEl<HTMLDivElement>('#reelsPreview');
          if (reelsPreviewEl) {
            reelsPreviewEl.innerHTML = this.createSymbolPreview(reels);
          }
        } catch (e) {
          console.error('Invalid reels format');
        }
      }
    });

    this.getChildEl<HTMLButtonElement>('#applyReelsOnce')?.addEventListener('click', () => {
      const inputEl = this.getChildEl<HTMLInputElement>('#reels');
      if (inputEl && inputEl.value) {
        try {
          const reels = JSON.parse(inputEl.value);
          cheatManager.applyReelSet(reels, true);
        } catch (e) {
          console.error('Invalid reels format');
        }
      }
    });

    this.getChildEl<HTMLInputElement>('#customReelsName')?.addEventListener('keydown', (e) => {
      e.stopPropagation();
    });

    this.getChildEl<HTMLButtonElement>('#saveReels')?.addEventListener('click', () => {
      const popup = this.getChildEl<HTMLDivElement>('#saveReelsPopup');
      const reelNameInput = this.getChildEl<HTMLInputElement>('#customReelsName');
      if (popup) {
        popup.style.display = 'flex';
      }
      if (reelNameInput) {
        reelNameInput.focus();
        reelNameInput.value = '';
      }
    });

    this.getChildEl<HTMLButtonElement>('#saveReelsCancel')?.addEventListener('click', () => {
      const popup = this.getChildEl<HTMLDivElement>('#saveReelsPopup');
      if (popup) {
        popup.style.display = 'none';
      }
    });

    this.getChildEl<HTMLButtonElement>('#saveReelsConfirm')?.addEventListener('click', () => {
      const nameInput = this.getChildEl<HTMLInputElement>('#customReelsName');
      const inputEl = this.getChildEl<HTMLInputElement>('#reels');
      if (nameInput && nameInput.value && inputEl && inputEl.value) {
        try {
          const savedReels = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
          const reels = JSON.parse(inputEl.value);

          const savedReel = {
            id: nameInput.value,
            value: reels,
            isLocal: true,
          };

          savedReels.push(savedReel);

          localStorage.setItem(this.storageKey, JSON.stringify(savedReels));
          const popup = this.getChildEl<HTMLDivElement>('#saveReelsPopup');
          if (popup) {
            popup.style.display = 'none';

            this.reels = [...this.reels, savedReel];

            const selectEl = this.getChildEl<HTMLSelectElement>('#predefinedReels');
            if (selectEl) {
              selectEl.innerHTML = this.renderReelsOptions(this.reels);
            }
          }
        } catch (e) {
          alert('Invalid reels format, not saved.');
        }
      } else {
        alert('Please enter a name.');
      }
    });

    this.getChildEl<HTMLButtonElement>('#deleteReels')?.addEventListener('click', () => {
      const selectEl = this.getChildEl<HTMLSelectElement>('#predefinedReels');
      const selectedOption = selectEl?.selectedOptions[0];
      const selectedId = selectedOption?.getAttribute('data-id');

      if (selectedId) {
        this.reels = this.reels.filter((reel) => reel.id !== selectedId);

        const newSavedReels = this.reels.filter(
          (reel: IPredefinedReel) => reel.isLocal && reel.id !== selectedId
        );

        localStorage.setItem(this.storageKey, JSON.stringify(newSavedReels));

        const selectEl = this.getChildEl<HTMLSelectElement>('#predefinedReels');
        if (selectEl) {
          selectEl.innerHTML = this.renderReelsOptions(this.reels);
          selectEl.selectedOptions[0].selected = true;
          selectEl.dispatchEvent(new Event('change'));
        }
      }
    });

    this.getChildEl<HTMLSelectElement>('#predefinedReels')?.addEventListener('change', (e) => {
      const selectEl = e.target as HTMLSelectElement;
      if (selectEl.selectedOptions.length > 0) {
        const reels = JSON.parse(selectEl.value);
        const inputEl = this.getChildEl<HTMLInputElement>('#reels');

        if (inputEl) {
          inputEl.value = JSON.stringify(reels);
        }

        const isLocal = selectEl.selectedOptions[0].getAttribute('data-is-local') === 'true';
        const deleteReelsEl = this.getChildEl<HTMLButtonElement>('#deleteReels');

        if (deleteReelsEl) {
          deleteReelsEl.style.display = isLocal ? 'flex' : 'none';
        }

        const previewEl = this.getChildEl<HTMLDivElement>('#reelsPreview');
        if (previewEl) {
          previewEl.innerHTML = this.createSymbolPreview(reels);
        }
      }
    });

    this.updateSelectedIcon();

    // Add click handler for reels preview to replace icons
    const reelsPreviewEl = this.getChildEl<HTMLDivElement>('#reelsPreview');
    if (reelsPreviewEl) {
      reelsPreviewEl.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const symbolContainer = target.closest('.symbol-container') as HTMLElement;

        if (symbolContainer && this.selectedIcon !== null) {
          const reelsInput = this.getChildEl<HTMLInputElement>('#reels');
          if (reelsInput) {
            try {
              const reels = JSON.parse(reelsInput.value);
              const reelIndex = parseInt(symbolContainer.dataset.reel || '-1');
              const symbolIndex = parseInt(symbolContainer.dataset.symbol || '-1');

              if (reelIndex >= 0 && symbolIndex >= 0) {
                // Update the reels data with a single digit instead of an array
                reels[reelIndex][symbolIndex] =
                  this.selectedIcon === '' ? 0 : parseInt(this.selectedIcon);
                reelsInput.value = JSON.stringify(reels);

                // Update the preview
                reelsPreviewEl.innerHTML = this.createSymbolPreview(reels);
              }
            } catch (e) {
              console.error('Error updating reels:', e);
            }
          }
        }
      });
    }
  }

  private updateSelectedIcon(): void {
    const iconOptions = this.getAllChildEl<HTMLElement>('.icon-option');
    iconOptions.forEach((option) => {
      option.addEventListener('click', () => {
        const iconId = option.getAttribute('data-icon');
        this.selectedIcon = iconId || null;
        iconOptions.forEach((opt) => opt.classList.remove('selected'));
        option.classList.add('selected');

        // Update selected icon info
        const infoEl = this.getChildEl<HTMLDivElement>('#selectedIconInfo');
        if (infoEl) {
          infoEl.textContent = iconId ? `Selected icon: ${iconId}` : 'Selected: Empty icon';
        }
      });
    });
  }

  private createIconSelectionUI(): string {
    const slotGameSettings = this.container.resolve<SlotGameSettings>(SLOT_GAME_SETTINGS_TOKEN);
    const iconMap = slotGameSettings.getSymbolIconMap();
    let html = '<div style="margin-top: 12px;">';
    html +=
      '<div style="font-size: 12px; color: white; margin-bottom: 8px;">Select icon to replace:</div>';
    html += '<div style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px;">';

    // Add empty icon option
    html += `<div class="icon-option" data-icon="" style="width: 40px; height: 40px; background: rgba(255,255,255,0.1); border: 2px solid transparent; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center;">
    <span style="color: white;">∅</span>
  </div>`;

    // Add only numeric icons
    Object.entries(iconMap).forEach(([symbolId, iconUrl]) => {
      // Only include if symbolId is a valid number
      if (!isNaN(Number(symbolId))) {
        html += `<div class="icon-option" data-icon="${symbolId}" style="width: 40px; height: 40px; background: rgba(255,255,255,0.1); border: 2px solid transparent; border-radius: 4px; cursor: pointer;">
        <img src="${this.assetsUrlFormatter?.format(iconUrl) || iconUrl}" alt="Symbol ${symbolId}" style="width: 100%; height: 100%; object-fit: contain;">
      </div>`;
      }
    });

    html += '</div>';
    html +=
      '<div id="selectedIconInfo" style="font-size: 12px; color: #00ff95; margin-bottom: 8px;"></div>';
    html += '</div>';

    // Add CSS for selected state
    html += `
    <style>
      .icon-option.selected {
        border: 2px solid #00ff95 !important;
        background: rgba(0, 255, 149, 0.1) !important;
      }
    </style>
  `;
    return html;
  }

  private createSymbolPreview(reels: number[][][]): string {
    const slotGameSettings = this.container.resolve<SlotGameSettings>(SLOT_GAME_SETTINGS_TOKEN);

    const iconMap = slotGameSettings.getSymbolIconMap();
    let preview = '<div style="display: flex; flex-wrap: wrap; gap: 4px; margin: 8px 0;">';

    reels.forEach((reel, reelIndex) => {
      preview += `<div class="reel-container" data-reel="${reelIndex}" style="display: flex; flex-direction: column; gap: 4px;">`;
      reel.forEach((symbol: number | number[], symbolIndex) => {
        // Handle both array and single number cases
        const symbolId = Array.isArray(symbol) ? symbol[0]?.toString() : symbol.toString();
        const iconUrl = iconMap[symbolId] || '';
        preview += `<div class="symbol-container" data-reel="${reelIndex}" data-symbol="${symbolIndex}" style="width: 30px; height: 30px; background: rgba(255,255,255,0.1); border-radius: 4px; cursor: pointer;">`;
        if (iconUrl) {
          preview += `<img src="${this.assetsUrlFormatter?.format(iconUrl) || iconUrl}" alt="Symbol ${symbolId}" style="width: 100%; height: 100%; object-fit: contain;">`;
        }
        preview += '</div>';
      });
      preview += '</div>';
    });

    preview += '</div>';
    return preview;
  }
}
